import { useState, useContext, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "@/App";
import { ArrowLeft, CheckCircle, AlertCircle, Loader, ChevronDown, Search, Hand, Fingerprint, Zap, SkipForward } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import SessionTimer from "@/components/SessionTimer";

// Capture mode configurations - Matching Suprema RealScan G10 specifications
const CAPTURE_MODES = [
  { 
    id: "single_flat", 
    label: "Single Flats", 
    description: "One finger flat on platen",
    fingers: 1,
    icon: "☝️"
  },
  { 
    id: "single_roll", 
    label: "Single Rolls", 
    description: "Roll finger edge to edge",
    fingers: 1,
    icon: "🔄"
  },
  { 
    id: "four_finger_slap", 
    label: "Four Finger Slaps", 
    description: "4 fingers simultaneously",
    fingers: 4,
    icon: "🖐️"
  },
  { 
    id: "two_thumbs", 
    label: "Two Thumbs", 
    description: "Both thumbs together",
    fingers: 2,
    icon: "👍"
  },
];

// Finger group definitions for multi-finger capture
const FINGER_GROUPS = {
  four_finger_slap: [
    { id: "right_slap", label: "Right Hand Slap", fingers: ["right_index", "right_middle", "right_ring", "right_pinky"] },
    { id: "left_slap", label: "Left Hand Slap", fingers: ["left_index", "left_middle", "left_ring", "left_pinky"] },
  ],
  two_thumbs: [
    { id: "both_thumbs", label: "Both Thumbs", fingers: ["right_thumb", "left_thumb"] },
  ],
};

// Standard capture order for batch mode (FBI standard order)
const CAPTURE_ORDER = [
  "right_thumb", "right_index", "right_middle", "right_ring", "right_pinky",
  "left_thumb", "left_index", "left_middle", "left_ring", "left_pinky"
];

const FingerprintCapturePage = () => {
  const { user } = useContext(AuthContext);
  const [step, setStep] = useState("select");
  const [captureMode, setCaptureMode] = useState("single_flat");
  const [fingerPosition, setFingerPosition] = useState("");
  const [fingerGroup, setFingerGroup] = useState(null);
  const [capturedData, setCapturedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [enrolledFingers, setEnrolledFingers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [quickCaptureMode, setQuickCaptureMode] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 10 });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchClients();
    const params = new URLSearchParams(location.search);
    const clientId = params.get('client_id');
    if (clientId) {
      axios.get(`/clients/${clientId}`)
        .then(response => {
          setSelectedClient(response.data);
          fetchClientFingerprints(clientId);
        })
        .catch(err => console.error("Failed to fetch client:", err));
    }
  }, [location]);

  const fetchClients = async () => {
    try {
      const response = await axios.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const fetchClientFingerprints = async (clientId) => {
    try {
      const response = await axios.get(`/clients/${clientId}/fingerprints`);
      setEnrolledFingers(response.data.map(fp => fp.finger_position));
    } catch (error) {
      console.error("Failed to fetch fingerprints:", error);
    }
  };

  const handleClientSelect = async (client) => {
    setSelectedClient(client);
    setDropdownOpen(false);
    setSearchTerm("");
    if (client) {
      await fetchClientFingerprints(client.id);
    } else {
      setEnrolledFingers([]);
    }
  };

  const filteredClients = clients.filter(client => {
    const fullName = `${client.first_name} ${client.middle_name || ''} ${client.last_name}`.toLowerCase();
    const email = (client.email || '').toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase());
  });

  const fingerPositions = [
    { value: "right_thumb", label: "R. Thumb", hand: "right" },
    { value: "right_index", label: "R. Index", hand: "right" },
    { value: "right_middle", label: "R. Middle", hand: "right" },
    { value: "right_ring", label: "R. Ring", hand: "right" },
    { value: "right_pinky", label: "R. Pinky", hand: "right" },
    { value: "left_thumb", label: "L. Thumb", hand: "left" },
    { value: "left_index", label: "L. Index", hand: "left" },
    { value: "left_middle", label: "L. Middle", hand: "left" },
    { value: "left_ring", label: "L. Ring", hand: "left" },
    { value: "left_pinky", label: "L. Pinky", hand: "left" },
  ];

  const handleCapture = async () => {
    if (!selectedClient) {
      toast.error("Select a client first");
      return;
    }
    
    if (captureMode === "single_flat" || captureMode === "single_roll") {
      if (!fingerPosition) {
        toast.error("Select a finger position");
        return;
      }
    } else {
      if (!fingerGroup) {
        toast.error("Select a finger group");
        return;
      }
    }

    setLoading(true);
    setError("");
    setStep("capture");

    try {
      const response = await axios.post("/fingerprint/capture", {
        capture_mode: captureMode
      });
      if (response.data.success) {
        setCapturedData(response.data);
        setStep("enroll");
      } else {
        setError("Capture failed");
        setStep("select");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Capture failed");
      setStep("select");
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!capturedData || !selectedClient) return;
    setLoading(true);

    try {
      // For multi-finger modes, enroll each finger
      const fingersToEnroll = captureMode === "single_flat" || captureMode === "single_roll" 
        ? [fingerPosition]
        : fingerGroup.fingers;

      for (const finger of fingersToEnroll) {
        await axios.post("/fingerprint/enroll", {
          user_id: user.id,
          client_id: selectedClient.id,
          finger_position: finger,
          template_data: capturedData.template,
          image_quality: capturedData.quality,
          capture_mode: captureMode,
        });
      }
      
      const newEnrolledFingers = [...enrolledFingers, ...fingersToEnroll];
      setEnrolledFingers(newEnrolledFingers);
      
      // In Quick Capture mode, auto-advance to next finger
      if (quickCaptureMode && (captureMode === "single_flat" || captureMode === "single_roll")) {
        advanceToNextFinger(newEnrolledFingers);
      } else {
        setStep("success");
        toast.success(`${fingersToEnroll.length} fingerprint(s) enrolled successfully!`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Enrollment failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep("select");
    setFingerPosition(quickCaptureMode ? fingerPosition : "");
    setFingerGroup(null);
    setCapturedData(null);
    setError("");
  };

  const handleModeChange = (mode) => {
    setCaptureMode(mode);
    setFingerPosition("");
    setFingerGroup(null);
  };

  const getClientName = (client) => {
    const middle = client.middle_name && client.middle_name !== 'NMN' ? ` ${client.middle_name.charAt(0)}.` : '';
    return `${client.first_name}${middle} ${client.last_name}`;
  };

  const isEnrolled = (pos) => enrolledFingers.includes(pos);
  const isSelected = (pos) => fingerPosition === pos;
  const isGroupSelected = (group) => fingerGroup?.id === group.id;
  const isGroupFullyEnrolled = (group) => group.fingers.every(f => enrolledFingers.includes(f));
  const isGroupPartiallyEnrolled = (group) => group.fingers.some(f => enrolledFingers.includes(f)) && !isGroupFullyEnrolled(group);

  const getEnrollmentProgress = () => {
    return `${enrolledFingers.length}/10 fingers enrolled`;
  };

  // Get the next unenrolled finger in standard order
  const getNextUnenrolledFinger = (currentEnrolled) => {
    for (const finger of CAPTURE_ORDER) {
      if (!currentEnrolled.includes(finger)) {
        return finger;
      }
    }
    return null; // All fingers enrolled
  };

  // Get finger label for display
  const getFingerLabel = (fingerValue) => {
    const finger = fingerPositions.find(f => f.value === fingerValue);
    return finger?.label || fingerValue;
  };

  // Start batch capture - auto-select first unenrolled finger
  const startBatchCapture = () => {
    const nextFinger = getNextUnenrolledFinger(enrolledFingers);
    if (nextFinger) {
      setCaptureMode("single_flat");
      setFingerPosition(nextFinger);
      setQuickCaptureMode(true);
      updateBatchProgress(enrolledFingers);
      toast.success(`Quick Capture started! First: ${getFingerLabel(nextFinger)}`);
    } else {
      toast.success("All 10 fingerprints already enrolled!");
    }
  };

  // Update batch progress indicator
  const updateBatchProgress = (currentEnrolled) => {
    const remaining = CAPTURE_ORDER.filter(f => !currentEnrolled.includes(f)).length;
    setBatchProgress({ current: 10 - remaining, total: 10 });
  };

  // Handle advancing to next finger in batch mode
  const advanceToNextFinger = (newEnrolledFingers) => {
    const nextFinger = getNextUnenrolledFinger(newEnrolledFingers);
    if (nextFinger) {
      setFingerPosition(nextFinger);
      setFingerGroup(null);
      setCapturedData(null);
      setStep("select");
      updateBatchProgress(newEnrolledFingers);
      toast(`Next: ${getFingerLabel(nextFinger)}`, { icon: '👆' });
    } else {
      // All fingers enrolled!
      setQuickCaptureMode(false);
      setStep("batch_complete");
      toast.success("All 10 fingerprints captured!");
    }
  };

  // Skip current finger in batch mode
  const skipCurrentFinger = () => {
    const currentIndex = CAPTURE_ORDER.indexOf(fingerPosition);
    const remainingFingers = CAPTURE_ORDER.slice(currentIndex + 1).filter(f => !enrolledFingers.includes(f));
    
    if (remainingFingers.length > 0) {
      setFingerPosition(remainingFingers[0]);
      toast(`Skipped. Next: ${getFingerLabel(remainingFingers[0])}`, { icon: '⏭️' });
    } else {
      // Check if there are any unenrolled fingers before the skipped one
      const nextFinger = getNextUnenrolledFinger(enrolledFingers);
      if (nextFinger && nextFinger !== fingerPosition) {
        setFingerPosition(nextFinger);
        toast(`Skipped. Next: ${getFingerLabel(nextFinger)}`, { icon: '⏭️' });
      } else {
        toast("No more fingers to capture", { icon: 'ℹ️' });
        setQuickCaptureMode(false);
      }
    }
  };

  // Exit batch mode
  const exitBatchMode = () => {
    setQuickCaptureMode(false);
    setFingerPosition("");
    toast("Quick Capture ended", { icon: '✋' });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-slate-800 text-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/dashboard" className="mr-4 hover:text-slate-300">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg font-semibold">Fingerprint Capture</h1>
          </div>
          <div className="flex items-center space-x-4">
            {selectedClient && (
              <span className="text-sm text-slate-400">
                {getEnrollmentProgress()}
              </span>
            )}
            <SessionTimer enabled={true} />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-2 rounded mb-4 text-sm flex items-center">
            <AlertCircle className="w-4 h-4 mr-2" />
            {error}
          </div>
        )}

        {step === "select" && (
          <div className="space-y-4">
            {/* Client Selection */}
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Client <span className="text-red-500">*</span>
              </label>
              
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded text-left flex items-center justify-between bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <span className={selectedClient ? "text-slate-900" : "text-slate-400"}>
                    {selectedClient ? getClientName(selectedClient) : "Select client..."}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {dropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded shadow-lg">
                    <div className="p-2 border-b border-slate-100">
                      <div className="relative">
                        <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredClients.length > 0 ? (
                        filteredClients.map((client) => (
                          <button
                            key={client.id}
                            onClick={() => handleClientSelect(client)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex justify-between items-center"
                          >
                            <span className="text-slate-900">{getClientName(client)}</span>
                            <span className="text-slate-400 text-xs">{client.total_fingerprints || 0} prints</span>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-slate-500">No clients found</div>
                      )}
                    </div>
                    <div className="p-2 border-t border-slate-100">
                      <Link to="/clients" className="text-blue-600 text-sm hover:underline">+ Add new client</Link>
                    </div>
                  </div>
                )}
              </div>

              {selectedClient && (
                <p className="mt-2 text-sm text-slate-600">
                  {selectedClient.email || selectedClient.phone || 'No contact info'}
                </p>
              )}
            </div>

            {/* Quick Capture Mode Banner */}
            {selectedClient && !quickCaptureMode && enrolledFingers.length < 10 && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Zap className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Quick Capture Mode</h3>
                      <p className="text-sm text-slate-600">
                        Capture all {10 - enrolledFingers.length} remaining fingerprints in sequence
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={startBatchCapture}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition flex items-center space-x-2"
                    data-testid="start-quick-capture-btn"
                  >
                    <Zap className="w-4 h-4" />
                    <span>Start</span>
                  </button>
                </div>
              </div>
            )}

            {/* Quick Capture Active Banner */}
            {selectedClient && quickCaptureMode && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center animate-pulse">
                      <Zap className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-emerald-800">Quick Capture Active</h3>
                        <span className="text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full">
                          {batchProgress.current}/{batchProgress.total}
                        </span>
                      </div>
                      <p className="text-sm text-emerald-700">
                        Now capturing: <span className="font-medium">{getFingerLabel(fingerPosition)}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={skipCurrentFinger}
                      className="px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition flex items-center space-x-1"
                      data-testid="skip-finger-btn"
                    >
                      <SkipForward className="w-4 h-4" />
                      <span>Skip</span>
                    </button>
                    <button
                      onClick={exitBatchMode}
                      className="px-3 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition"
                      data-testid="exit-quick-capture-btn"
                    >
                      Exit
                    </button>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-emerald-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 transition-all duration-300"
                      style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    {10 - enrolledFingers.length} fingerprints remaining
                  </p>
                </div>
              </div>
            )}

            {/* Capture Mode Selection - only show when client selected and not in quick capture mode */}
            {selectedClient && !quickCaptureMode && (
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Capture Mode
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {CAPTURE_MODES.map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => handleModeChange(mode.id)}
                      className={`p-3 rounded-lg border-2 transition text-center ${
                        captureMode === mode.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-2xl block mb-1">{mode.icon}</span>
                      <span className="text-sm font-medium text-slate-900 block">{mode.label}</span>
                      <span className="text-xs text-slate-500">{mode.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Finger Selection - varies by capture mode, hidden in quick capture mode */}
            {selectedClient && !quickCaptureMode && (
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  {captureMode === "single_flat" || captureMode === "single_roll" 
                    ? "Select Finger" 
                    : "Select Finger Group"
                  } <span className="text-red-500">*</span>
                </label>
                
                {/* Single Flats or Single Rolls - Show individual fingers */}
                {(captureMode === "single_flat" || captureMode === "single_roll") && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Right Hand */}
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2 flex items-center">
                        <Hand className="w-3 h-3 mr-1" /> Right Hand
                      </p>
                      <div className="space-y-1">
                        {fingerPositions.filter(f => f.hand === "right").map((finger) => (
                          <button
                            key={finger.value}
                            onClick={() => !isEnrolled(finger.value) && setFingerPosition(finger.value)}
                            disabled={isEnrolled(finger.value)}
                            className={`w-full px-3 py-2 text-sm rounded border transition flex items-center justify-between ${
                              isEnrolled(finger.value)
                                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                : isSelected(finger.value)
                                ? "bg-blue-50 border-blue-500 text-blue-700"
                                : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"
                            }`}
                          >
                            <span>{finger.label}</span>
                            {isEnrolled(finger.value) && <CheckCircle className="w-4 h-4" />}
                            {isSelected(finger.value) && !isEnrolled(finger.value) && (
                              <Fingerprint className="w-4 h-4" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Left Hand */}
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2 flex items-center">
                        <Hand className="w-3 h-3 mr-1 scale-x-[-1]" /> Left Hand
                      </p>
                      <div className="space-y-1">
                        {fingerPositions.filter(f => f.hand === "left").map((finger) => (
                          <button
                            key={finger.value}
                            onClick={() => !isEnrolled(finger.value) && setFingerPosition(finger.value)}
                            disabled={isEnrolled(finger.value)}
                            className={`w-full px-3 py-2 text-sm rounded border transition flex items-center justify-between ${
                              isEnrolled(finger.value)
                                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                : isSelected(finger.value)
                                ? "bg-blue-50 border-blue-500 text-blue-700"
                                : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"
                            }`}
                          >
                            <span>{finger.label}</span>
                            {isEnrolled(finger.value) && <CheckCircle className="w-4 h-4" />}
                            {isSelected(finger.value) && !isEnrolled(finger.value) && (
                              <Fingerprint className="w-4 h-4" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Four Finger Slaps or Two Thumbs - Show finger groups */}
                {(captureMode === "four_finger_slap" || captureMode === "two_thumbs") && (
                  <div className={`grid ${captureMode === "two_thumbs" ? "grid-cols-1 max-w-md" : "grid-cols-2"} gap-3`}>
                    {FINGER_GROUPS[captureMode].map((group) => (
                      <button
                        key={group.id}
                        onClick={() => !isGroupFullyEnrolled(group) && setFingerGroup(group)}
                        disabled={isGroupFullyEnrolled(group)}
                        className={`p-4 rounded-lg border-2 transition text-left ${
                          isGroupFullyEnrolled(group)
                            ? "bg-emerald-50 border-emerald-200"
                            : isGroupSelected(group)
                            ? "bg-blue-50 border-blue-500"
                            : isGroupPartiallyEnrolled(group)
                            ? "bg-amber-50 border-amber-200"
                            : "bg-white border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900">{group.label}</span>
                          {isGroupFullyEnrolled(group) && (
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {group.fingers.map(f => {
                            const finger = fingerPositions.find(fp => fp.value === f);
                            return (
                              <span
                                key={f}
                                className={`text-xs px-2 py-0.5 rounded ${
                                  isEnrolled(f)
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-slate-100 text-slate-600"
                                }`}
                              >
                                {finger?.label}
                              </span>
                            );
                          })}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Capture mode info */}
                <div className="mt-4 p-3 bg-slate-50 rounded text-sm text-slate-600">
                  {captureMode === "single_flat" && (
                    <p>Place single finger flat on platen. Image: 800 x 750 pixels at 500 dpi.</p>
                  )}
                  {captureMode === "single_roll" && (
                    <p>Roll finger from nail edge to nail edge. Image: 800 x 750 pixels. Required for FBI IAFIS.</p>
                  )}
                  {captureMode === "four_finger_slap" && (
                    <p>Place four fingers flat on platen (excluding thumb). Image: 1600 x 1500 pixels at 500 dpi.</p>
                  )}
                  {captureMode === "two_thumbs" && (
                    <p>Place both thumbs flat on platen simultaneously. Platen size: 81.28 x 76.2 mm.</p>
                  )}
                </div>
              </div>
            )}

            {/* Capture Button - Standard mode */}
            {selectedClient && !quickCaptureMode && (
              <button
                onClick={handleCapture}
                disabled={loading || (!fingerPosition && !fingerGroup)}
                className="w-full bg-slate-800 text-white py-3 rounded-lg font-medium hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center space-x-2"
                data-testid="start-capture-btn"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Capturing...</span>
                  </>
                ) : (
                  <>
                    <Fingerprint className="w-5 h-5" />
                    <span>Start Capture</span>
                  </>
                )}
              </button>
            )}

            {/* Capture Button - Quick Capture mode */}
            {selectedClient && quickCaptureMode && (
              <button
                onClick={handleCapture}
                disabled={loading}
                className="w-full bg-emerald-600 text-white py-3 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center space-x-2"
                data-testid="quick-capture-btn"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Capturing...</span>
                  </>
                ) : (
                  <>
                    <Fingerprint className="w-5 h-5" />
                    <span>Capture {getFingerLabel(fingerPosition)}</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {step === "capture" && (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <div className="w-24 h-24 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
              <Fingerprint className="w-12 h-12 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {captureMode === "single_roll" ? "Roll finger now..." : "Place finger(s) on platen..."}
            </h3>
            <p className="text-slate-600 text-sm">
              {captureMode === "single_flat" && "Keep finger flat and steady until capture completes"}
              {captureMode === "single_roll" && "Roll finger slowly from nail edge to nail edge"}
              {captureMode === "four_finger_slap" && "Place all four fingers flat and steady"}
              {captureMode === "two_thumbs" && "Place both thumbs flat and steady"}
            </p>
          </div>
        )}

        {step === "enroll" && capturedData && (
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-3 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Capture Successful</h3>
              <p className="text-slate-600 text-sm mt-1">
                Quality Score: <span className="font-medium">{capturedData.quality}%</span>
              </p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Client:</span>
                  <span className="ml-2 font-medium text-slate-900">{getClientName(selectedClient)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Mode:</span>
                  <span className="ml-2 font-medium text-slate-900">
                    {CAPTURE_MODES.find(m => m.id === captureMode)?.label}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Finger(s):</span>
                  <span className="ml-2 font-medium text-slate-900">
                    {captureMode === "single_flat" || captureMode === "single_roll"
                      ? fingerPositions.find(f => f.value === fingerPosition)?.label
                      : fingerGroup?.label
                    }
                  </span>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleReset}
                className="flex-1 py-2.5 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
              >
                Retake
              </button>
              <button
                onClick={handleEnroll}
                disabled={loading}
                className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 transition flex items-center justify-center"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  "Save & Enroll"
                )}
              </button>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Enrollment Complete!</h3>
            <p className="text-slate-600 mb-6">
              {captureMode === "single_flat" || captureMode === "single_roll"
                ? "Fingerprint has been enrolled successfully."
                : `${fingerGroup?.fingers.length} fingerprints have been enrolled successfully.`
              }
            </p>
            <p className="text-sm text-slate-500 mb-6">
              {getEnrollmentProgress()}
            </p>
            <div className="flex space-x-3 justify-center">
              <button
                onClick={handleReset}
                className="px-6 py-2.5 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-900 transition"
              >
                Capture Another
              </button>
              <Link
                to="/clients"
                className="px-6 py-2.5 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
              >
                Back to Clients
              </Link>
            </div>
          </div>
        )}

        {/* Batch Complete - All 10 fingerprints captured */}
        {step === "batch_complete" && (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center">
              <div className="relative">
                <CheckCircle className="w-12 h-12 text-emerald-600" />
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">All Fingerprints Captured!</h3>
            <p className="text-slate-600 mb-2">
              Successfully enrolled all 10 fingerprints for
            </p>
            <p className="text-lg font-semibold text-slate-900 mb-6">
              {selectedClient ? getClientName(selectedClient) : 'Client'}
            </p>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 inline-block">
              <div className="flex items-center space-x-2 text-emerald-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">10/10 fingers enrolled</span>
              </div>
            </div>

            <div className="flex space-x-3 justify-center">
              <Link
                to={`/clients/${selectedClient?.id}`}
                className="px-6 py-2.5 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-900 transition"
              >
                View Client Profile
              </Link>
              <Link
                to="/clients"
                className="px-6 py-2.5 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
              >
                Back to Clients
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default FingerprintCapturePage;
